import { forwardRef } from "react";
import type { TreeItemComponentProps } from "dnd-kit-sortable-tree";
import { SimpleTreeItemWrapper } from "dnd-kit-sortable-tree";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { Icon } from "@circle-react-shared/Icon";

export interface TreeItem {
  canHaveChildren?: boolean;
  children?: TreeItem[];
  collapsed?: boolean;
  id: string;
  type: string;
}

export const LayersTreeItem = forwardRef<
  HTMLDivElement,
  TreeItemComponentProps<TreeItem>
  // eslint-disable-next-line @typescript-eslint/unbound-method -- False positive, arrow function
>(({ item, depth, clone, ghost, isOver, onCollapse, ...props }, ref) => {
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = hasChildren && !item.collapsed;

  if (clone) {
    return null;
  }

  return (
    <SimpleTreeItemWrapper
      {...props}
      ref={ref}
      item={item}
      depth={depth}
      clone={clone}
      ghost={ghost}
      isOver={isOver}
      className={classNames("hover:bg-secondary transition-colors", {
        "!opacity-100": ghost,
      })}
      contentClassName={classNames("!border-none !p-0", {
        "bg-secondary": ghost,
      })}
      aria-level={depth}
      aria-grabbed={clone ? true : undefined}
      aria-dropeffect={isOver ? "move" : undefined}
      showDragHandle={false}
    >
      {hasChildren ? (
        <button
          type="button"
          className="flex w-full items-center gap-2 px-3 py-2 text-left"
          aria-expanded={isExpanded}
          onClick={onCollapse}
        >
          <span className="flex">
            <Icon
              type="16-chevron-right"
              size={16}
              className={classNames("size-4 transition-all", {
                "rotate-90": isExpanded,
              })}
            />
          </span>
          <span className="truncate">{item.type}</span>
        </button>
      ) : (
        <div className="flex w-full items-center gap-2 px-3 py-2">
          <span className="truncate">{item.type}</span>
        </div>
      )}
    </SimpleTreeItemWrapper>
  );
});

LayersTreeItem.displayName = "LayersTreeItem";
